/**
 * File Validation Utility
 * 
 * This module provides comprehensive file validation including:
 * - Corruption detection
 * - Password protection detection
 * - File integrity checks
 * 
 * Supports: PDF, Excel (xlsx, xls, csv), Word (doc, docx), Images (png, jpg, jpeg), Text files
 */

// import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import ExcelJS from 'exceljs';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';

// Initialize PDF.js worker
// GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist/build/pdf.worker.min.js';

/**
 * Result of file validation
 */
export interface FileValidationResult {
  isValid: boolean;
  isProtected: boolean;
  isCorrupted: boolean;
  error?: string;
}




/**
 * Result of batch file validation
 */
export interface BatchValidationResult {
  validFiles: File[];
  invalidFiles: { file: File; reason: string }[];
}

/**
 * Result of duplicate file check
 */
export interface DuplicateCheckResult {
  uniqueFiles: File[];
  duplicateFiles: File[];
}

/**
 * Check if a PDF file is password protected or corrupted
 */
// export const validatePdfFile = (file: File): Promise<FileValidationResult> => {
//   return new Promise((resolve) => {
//     const fileReader = new FileReader();

//     fileReader.onload = async (e) => {
//       try {
//         const typedArray = new Uint8Array(e.target?.result as ArrayBuffer);
//         const loadingTask = getDocument({ data: typedArray });

//         await loadingTask.promise.then(
//           () => resolve({ isValid: true, isProtected: false, isCorrupted: false }),
//           (error) => {
//             if (error.name === 'PasswordException') {
//               resolve({ isValid: false, isProtected: true, isCorrupted: false, error: 'File is password protected' });
//             } else if (error.name === 'InvalidPDFException') {
//               resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'File is corrupted or invalid' });
//             } else {
//               resolve({ isValid: false, isProtected: false, isCorrupted: true, error: error.message || 'Unknown error' });
//             }
//           }
//         );
//       } catch (error) {
//         resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'Failed to process PDF file' });
//       }
//     };

//     fileReader.onerror = () => {
//       resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'Failed to read file' });
//     };

//     fileReader.readAsArrayBuffer(file);
//   });
// };


export const validatePdfFile = (file: File): Promise<FileValidationResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        const bytes = new Uint8Array(buffer);

        // Check PDF magic bytes: must start with %PDF-
        const isPDF =
          bytes[0] === 0x25 && // %
          bytes[1] === 0x50 && // P
          bytes[2] === 0x44 && // D
          bytes[3] === 0x46 && // F
          bytes[4] === 0x2D;   // -

        if (!isPDF) {
          resolve({
            isValid: false,
            isProtected: false,
            isCorrupted: true,
            error: 'File is corrupted or invalid',
          });
          return;
        }

        // Scan for encryption dictionary — present in password-protected PDFs
        // Decode as latin1 to safely handle binary content without charset issues
        const text = new TextDecoder('latin1').decode(bytes);
        const isEncrypted =
          text.includes('/Encrypt') ||
          text.includes('/EncryptMetadata') ||
          text.includes('/StdCF');

        if (isEncrypted) {
          resolve({
            isValid: false,
            isProtected: true,
            isCorrupted: false,
            error: 'File is password protected',
          });
          return;
        }

        // Check PDF has a valid cross-reference table (basic structure check)
        const hasXref = text.includes('xref') || text.includes('startxref');
        if (!hasXref) {
          resolve({
            isValid: false,
            isProtected: false,
            isCorrupted: true,
            error: 'File is corrupted or invalid',
          });
          return;
        }

        resolve({ isValid: true, isProtected: false, isCorrupted: false });
      } catch (error: any) {
        console.error('[PDF Validation] Exception:', error);
        resolve({
          isValid: false,
          isProtected: false,
          isCorrupted: true,
          error: 'Failed to process PDF file',
        });
      }
    };

    reader.onerror = () => {
      resolve({
        isValid: false,
        isProtected: false,
        isCorrupted: true,
        error: 'Failed to read file',
      });
    };

    reader.readAsArrayBuffer(file);
  });
};


/**
 * Check if an Excel file is password protected or corrupted
 */
export const validateExcelFile = (file: File): Promise<FileValidationResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        // Security: migrated from `xlsx` (had unfixable HIGH CVEs) to `exceljs`.
        // Same validation purpose: load the file and reject if parsing fails.
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(e.target?.result as ArrayBuffer);
        resolve({ isValid: true, isProtected: false, isCorrupted: false });
      } catch (error: any) {
        const msg: string = error?.message || '';
        if (msg.includes('password-protected') || msg.includes('File is password-protected') || msg.includes('encrypted')) {
          resolve({ isValid: false, isProtected: true, isCorrupted: false, error: 'File is password protected' });
        } else {
          resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'File is corrupted or invalid' });
        }
      }
    };

    reader.onerror = () => {
      resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'Failed to read file' });
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Check if a Word document is password protected or corrupted
 */
export const validateWordFile = (file: File): Promise<FileValidationResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result;
        const zip = new PizZip(content as ArrayBuffer);
        new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });
        resolve({ isValid: true, isProtected: false, isCorrupted: false });
      } catch (error) {
        // Docxtemplater throws on both protected and corrupted files
        resolve({ isValid: false, isProtected: true, isCorrupted: true, error: 'File is password protected or corrupted' });
      }
    };

    reader.onerror = () => {
      resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'Failed to read file' });
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Check if an image file is corrupted
 */
export const validateImageFile = (file: File): Promise<FileValidationResult> => {
  return new Promise((resolve) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({ isValid: true, isProtected: false, isCorrupted: false });
    };
    
    img.onerror = () => {
      resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'Image file is corrupted or invalid' });
    };

    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'Failed to read file' });
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Check if a text file is corrupted or protected
 */
export const validateTextFile = (file: File): Promise<FileValidationResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;

        // Check for binary/corrupted content (non-printable characters)
        const isCorrupted = /[\x00-\x08\x0E-\x1F]/.test(content);
        if (isCorrupted) {
          resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'File appears to be corrupted' });
          return;
        }

        // Check for protection marker (custom check)
        const isProtected = content.startsWith('PROTECTED:');
        if (isProtected) {
          resolve({ isValid: false, isProtected: true, isCorrupted: false, error: 'File appears to be password protected' });
          return;
        }

        resolve({ isValid: true, isProtected: false, isCorrupted: false });
      } catch (error) {
        resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'Failed to process text file' });
      }
    };

    reader.onerror = () => {
      resolve({ isValid: false, isProtected: false, isCorrupted: true, error: 'Failed to read file' });
    };

    reader.readAsText(file);
  });
};

/**
 * Check if a pptx file is corrupted or protected
 */

const validatePptxFile = async (file: File): Promise<FileValidationResult> => {
  try {
    // Read first 8 bytes to cover both magic byte signatures
    const headerBuffer = await file.slice(0, 8).arrayBuffer();
    const headerBytes = new Uint8Array(headerBuffer);

    // Check OLE2 magic bytes (D0 CF 11 E0 A1 B1 1A E1) — password-protected pptx
    const isOle2 =
      headerBytes[0] === 0xd0 &&
      headerBytes[1] === 0xcf &&
      headerBytes[2] === 0x11 &&
      headerBytes[3] === 0xe0 &&
      headerBytes[4] === 0xa1 &&
      headerBytes[5] === 0xb1 &&
      headerBytes[6] === 0x1a &&
      headerBytes[7] === 0xe1;

    if (isOle2) {
      return { isValid: false, isProtected: true, isCorrupted: false, error: 'File is password protected' };
    }

    // Check ZIP magic bytes (PK header: 50 4B 03 04)
    const isPkZip =
      headerBytes[0] === 0x50 &&
      headerBytes[1] === 0x4b &&
      headerBytes[2] === 0x03 &&
      headerBytes[3] === 0x04;

    if (!isPkZip) {
      return { isValid: false, isProtected: false, isCorrupted: true, error: 'File is corrupted or not a valid .pptx' };
    }

    // Scan for "ppt/" directory entry to confirm it's a pptx, not a renamed zip
    const scanSize = Math.min(file.size, 8192);
    const scanBuffer = await file.slice(0, scanSize).arrayBuffer();
    const text = new TextDecoder('utf-8', { fatal: false }).decode(scanBuffer);
    const hasPptDir = text.includes('ppt/');

    if (!hasPptDir) {
      return { isValid: false, isProtected: false, isCorrupted: true, error: 'File does not appear to be a valid .pptx' };
    }

    return { isValid: true, isProtected: false, isCorrupted: false };
  } catch (error) {
    return { isValid: false, isProtected: false, isCorrupted: true, error: 'Failed to validate .pptx file' };
  }
};


/**
 * Get the file extension from a filename (lowercase)
 */
const getFileExtension = (filename: string): string => {
  if (!filename) return '';
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === 0) return '';
  return filename.slice(lastDot + 1).toLowerCase();
};

/**
 * Validate a single file based on its type
 * Returns validation result with details about corruption/protection status
 */
export const validateFile = async (file: File): Promise<FileValidationResult> => {
  // Check for empty files first
  if (file.size === 0) {
    return { isValid: false, isProtected: false, isCorrupted: true, error: 'File is empty' };
  }

  const extension = getFileExtension(file.name);

  // PDF validation
  if (extension === 'pdf') {
    return validatePdfFile(file);
  }

  // Excel/Spreadsheet validation
  const spreadsheetExtensions = ['xlsx', 'xls', 'xlsm', 'xlsb', 'csv', 'ods'];
  if (spreadsheetExtensions.includes(extension)) {
    return validateExcelFile(file);
  }

  // Word document validation (only .docx - .doc files skip validation and pass through to API)
  const wordExtensions = ['docx'];
  if (wordExtensions.includes(extension)) {
    return validateWordFile(file);
  }

  // PowerPoint validation (only .pptx - .ppt files skip validation and pass through to API)
  const pptxExtensions = ['pptx'];
  if (pptxExtensions.includes(extension)) {
    return validatePptxFile(file);
  }

  // Image validation
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
  if (imageExtensions.includes(extension)) {
    return validateImageFile(file);
  }

  // Text file validation
  if (extension === 'txt') {
    return validateTextFile(file);
  }

  // For unsupported file types, return valid (no specific validation available)
  return { isValid: true, isProtected: false, isCorrupted: false };
};

/**
 * Filter out duplicate files based on existing file names
 * Performs case-insensitive comparison
 * 
 * @param newFiles - Array of new files to check
 * @param existingFileNames - Array of existing file names to check against
 * @returns Object containing unique files and duplicate files
 */
export const filterDuplicateFiles = (
  newFiles: File[],
  existingFileNames: string[]
): DuplicateCheckResult => {
  const existingNamesSet = new Set(existingFileNames.map((name) => name.toLowerCase()));
  
  const uniqueFiles: File[] = [];
  const duplicateFiles: File[] = [];
  
  for (const file of newFiles) {
    if (existingNamesSet.has(file.name.toLowerCase())) {
      duplicateFiles.push(file);
    } else {
      uniqueFiles.push(file);
    }
  }
  
  return { uniqueFiles, duplicateFiles };
};

/**
 * Validate multiple files and separate valid from invalid
 * This is the main function to use before uploading files
 * 
 * @param files - Array of files to validate
 * @returns Object containing arrays of valid and invalid files with reasons
 */
export const validateFilesForUpload = async (files: File[]): Promise<BatchValidationResult> => {
  const validFiles: File[] = [];
  const invalidFiles: { file: File; reason: string }[] = [];

  // Process files in parallel for better performance
  const validationPromises = files.map(async (file) => {
    const result = await validateFile(file);
    return { file, result };
  });

  const results = await Promise.all(validationPromises);

  for (const { file, result } of results) {
    if (result.isValid) {
      validFiles.push(file);
    } else {
      let reason = result.error || 'Unknown validation error';
      if (result.isProtected && result.isCorrupted) {
        reason = 'File is password protected or corrupted';
      } else if (result.isProtected) {
        reason = 'File is password protected';
      } else if (result.isCorrupted) {
        reason = 'File is corrupted or invalid';
      }
      invalidFiles.push({ file, reason });
    }
  }

  return { validFiles, invalidFiles };
};

export default {
  validateFile,
  validateFilesForUpload,
  filterDuplicateFiles,
  validatePdfFile,
  validateExcelFile,
  validateWordFile,
  validateImageFile,
  validateTextFile,
};

